import { Button, Checkbox, Input, InputGroup, toast } from "@heroui/react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useSignUp } from "@clerk/react";
import { AlertCircle, Eye, EyeOff } from "lucide-react";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z"
      fill="#4285F4"
    />
    <path
      d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z"
      fill="#34A853"
    />
    <path
      d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"
      fill="#EA4335"
    />
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 72; 
const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

const MIN_NAME_LENGTH = 2;

const validateDetails = ({ firstName, lastName, email, password, agreedToTerms }) => {
  const errors = {};
  const first = firstName.trim();
  const last = lastName.trim();
  const mail = email.trim();

  if (!first) {
    errors.firstName = "Enter your first name.";
  } else if (first.length < MIN_NAME_LENGTH) {
    errors.firstName = "That first name is too short.";
  }

  if (!last) {
    errors.lastName = "Enter your last name.";
  } else if (last.length < MIN_NAME_LENGTH) {
    errors.lastName = "That last name is too short.";
  }

  if (!mail) {
    errors.email = "Enter your email address.";
  } else if (!EMAIL_RE.test(mail)) {
    errors.email = "That email address isn't valid.";
  }

  if (!password) {
    errors.password = "Choose a password.";
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  } else if (password.length > MAX_PASSWORD_LENGTH) {
    errors.password = `Keep it under ${MAX_PASSWORD_LENGTH} characters.`;
  }

  if (!agreedToTerms) {
    errors.terms = "Accept the Terms and Conditions to continue.";
  }

  return errors;
};

const validateCode = (code) => {
  const value = code.trim();
  if (!value) return { code: "Enter the code we emailed you." };
  if (!/^\d+$/.test(value)) return { code: "The code is digits only." };
  if (value.length !== CODE_LENGTH) return { code: `The code is ${CODE_LENGTH} digits.` };
  return {};
};

/* Pull a readable message off a Clerk error object or a thrown exception. */
const messageFrom = (err, fallback) =>
  err?.longMessage ||
  err?.message ||
  err?.errors?.[0]?.longMessage ||
  err?.errors?.[0]?.message ||
  fallback;

/* ---------------------------------------------------------------- */

const FieldError = ({ error }) => {
  const message = typeof error === "string" ? error : error?.message;
  return message ? <p className="text-xs text-danger">{message}</p> : null;
};

const SignUpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp, errors, fetchStatus } = useSignUp();
  const { isSignedIn } = useAuth();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [code, setCode] = useState("");

  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [activeAction, setActiveAction] = useState(null);
  const [showFormOverride, setShowFormOverride] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const isPending = fetchStatus === "fetching";
  const isBusy = activeAction !== null;

  const inFlight = useRef(false);

  useEffect(() => {
    if (isSignedIn) navigate("/", { replace: true });
  }, [isSignedIn, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const needsEmailVerification =
    signUp?.status === "missing_requirements" &&
    signUp.unverifiedFields?.includes("email_address") &&
    signUp.missingFields?.length === 0;

  const isVerifyStep = needsEmailVerification && !showFormOverride;

  const clearFieldError = (key) =>
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

  const reportValidation = (errs) => {
    setFieldErrors(errs);
    const first = Object.values(errs).find(Boolean);
    if (first) toast.warning(first);
    return Object.keys(errs).length === 0;
  };

  const sendCode = async ({ silent = false } = {}) => {
    const { error } = await signUp.verifications.sendEmailCode();
    if (error) {
      toast.danger(messageFrom(error, "Couldn't send the code. Try again in a moment."));
      return false;
    }
    setResendIn(RESEND_COOLDOWN_SECONDS);
    if (!silent) toast.success("Code sent. Check your inbox.");
    return true;
  };

  const finalizeAndRedirect = () =>
    signUp.finalize({
      navigate: ({ session, decorateUrl }) => {
        if (session?.currentTask) {
          console.log(session.currentTask);
          return;
        }
        const from = location.state?.from || "/";
        const url = decorateUrl(from);
        if (url.startsWith("http")) {
          window.location.href = url;
        } else {
          navigate(url, { replace: true });
        }
      },
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current || !signUp) return;

    setFormError(null);
    if (!reportValidation(validateDetails({ firstName, lastName, email, password, agreedToTerms }))) return;

    inFlight.current = true;
    setActiveAction("password");

    try {
      const { error } = await signUp.password({
        emailAddress: email.trim().toLowerCase(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      if (error) {
        // Field-level errors also render inline via `errors.fields`.
        toast.danger(messageFrom(error, "Couldn't create the account. Check your details and try again."));
        return;
      }

      await sendCode({ silent: true });
      setShowFormOverride(false);
    } catch (err) {
      const message = messageFrom(err, "Something went wrong. Try again.");
      setFormError(message);
      toast.danger(message);
    } finally {
      inFlight.current = false;
      setActiveAction(null);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (inFlight.current || !signUp) return;

    setFormError(null);
    if (!reportValidation(validateCode(code))) return;

    inFlight.current = true;
    setActiveAction("verify");

    try {
      const { error } = await signUp.verifications.verifyEmailCode({ code: code.trim() });
      if (error) {
        toast.danger(messageFrom(error, "That code didn't work. Check it and try again."));
        return;
      }

      if (signUp.status === "complete") {
        await finalizeAndRedirect();
      } else {
        console.error("Sign-up attempt not complete:", signUp);
        const message = "Sign up couldn't be completed. Start again or contact support.";
        setFormError(message);
        toast.danger(message);
      }
    } catch (err) {
      const message = messageFrom(err, "Something went wrong. Try again.");
      setFormError(message);
      toast.danger(message);
    } finally {
      inFlight.current = false;
      setActiveAction(null);
    }
  };

  const handleResendCode = async () => {
    if (inFlight.current || !signUp) return;

    if (resendIn > 0) {
      toast.warning(`Wait ${resendIn}s before requesting another code.`);
      return;
    }

    setFormError(null);
    inFlight.current = true;
    setActiveAction("resend");

    try {
      await sendCode();
    } catch (err) {
      toast.danger(messageFrom(err, "Couldn't resend the code. Try again."));
    } finally {
      inFlight.current = false;
      setActiveAction(null);
    }
  };

  const handleChangeEmail = () => {
    setShowFormOverride(true);
    setCode("");
    setFormError(null);
    setFieldErrors({});
  };

  const handleGoogleSignUp = async () => {
    if (inFlight.current || !signUp) return;

    if (!agreedToTerms) {
      setFieldErrors((prev) => ({ ...prev, terms: "Accept the Terms and Conditions to continue." }));
      toast.warning("Accept the Terms and Conditions to continue.");
      return;
    }

    setFormError(null);
    inFlight.current = true;
    setActiveAction("google");

    try {
      const from = location.state?.from || "/";
      sessionStorage.setItem("oauth_redirect", from);

      const { error } = await signUp.sso({
        strategy: "oauth_google",
        redirectCallbackUrl: "/sso-callback",
        redirectUrl: "/",
      });

      if (error) {
        const message = messageFrom(error, "Google sign up failed. Try again.");
        setFormError(message);
        toast.danger(message);
        inFlight.current = false;
        setActiveAction(null);
      }
      // No error means the browser is redirecting to Google — leave the button pending.
    } catch (err) {
      const message = messageFrom(err, "Google sign up failed. Try again.");
      setFormError(message);
      toast.danger(message);
      inFlight.current = false;
      setActiveAction(null);
    }
  };

  return (
    <div className="auth-page relative h-screen max-h-screen overflow-y-auto overflow-x-hidden">
      <img src="/bg-light.svg" alt="" className="h-screen w-screen absolute top-0" />
      <div className="flex flex-col md:justify-center items-center gap-10 section-padding-x relative min-h-screen w-screen pb-6 backdrop-blur-xl bg-backdrop/30">
        <div className="flex flex-col items-start md:mt-0 mt-28 lg:w-2/5 md:w-3/5 mx-auto gap-4">
          <div className="flex items-center">
            <div className="rounded-full">
              <img src="/logo-text.svg" width={68} alt="Loopp" />
            </div>
          </div>

          <div className="">
            <span className="font-bold leading-[36px] text-[40px] text-glass-foreground">
              {isVerifyStep ? (
                <>
                  Check <br /> your email
                </>
              ) : (
                <>
                  Create <br /> your account
                </>
              )}
            </span>
            {isVerifyStep && (
              <p className="text-sm text-glass-foreground-muted mt-2">
                We sent a code to <span className="font-semibold">{email.trim()}</span>
              </p>
            )}
          </div>
        </div>

        {!isVerifyStep ? (
          <form className="flex flex-col gap-10 lg:w-2/5 md:w-3/5 mx-auto" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                  <Input
                    placeholder="First Name"
                    className="auth-input"
                    fullWidth
                    autoComplete="given-name"
                    name="firstName"
                    value={firstName}
                    aria-invalid={Boolean(fieldErrors.firstName || errors?.fields?.firstName)}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      clearFieldError("firstName");
                    }}
                  />
                  <FieldError error={fieldErrors.firstName || errors?.fields?.firstName} />
                </div>

                <div className="flex flex-col gap-1">
                  <Input
                    placeholder="Last Name"
                    className="auth-input"
                    fullWidth
                    autoComplete="family-name"
                    name="lastName"
                    value={lastName}
                    aria-invalid={Boolean(fieldErrors.lastName || errors?.fields?.lastName)}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      clearFieldError("lastName");
                    }}
                  />
                  <FieldError error={fieldErrors.lastName || errors?.fields?.lastName} />
                </div>

                <div className="flex flex-col gap-1">
                  <Input
                    placeholder="Email"
                    className="auth-input"
                    fullWidth
                    autoComplete="email"
                    inputMode="email"
                    name="email"
                    value={email}
                    aria-invalid={Boolean(fieldErrors.email || errors?.fields?.emailAddress)}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearFieldError("email");
                    }}
                  />
                  <FieldError error={fieldErrors.email || errors?.fields?.emailAddress} />
                </div>

                <div className="flex flex-col gap-1">
                  <InputGroup className="auth-input">
                    <InputGroup.Input
                      placeholder="Password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      name="password"
                      value={password}
                      aria-invalid={Boolean(fieldErrors.password || errors?.fields?.password)}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearFieldError("password");
                      }}
                      className={"px-0 placeholder:text-glass-foreground"}
                    />
                    <InputGroup.Suffix>
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        className="text-background/70 hover:text-background transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </InputGroup.Suffix>
                  </InputGroup>
                  <FieldError error={fieldErrors.password || errors?.fields?.password} />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Checkbox
                  name="terms"
                  isSelected={agreedToTerms}
                  onChange={(selected) => {
                    setAgreedToTerms(selected);
                    clearFieldError("terms");
                  }}
                >
                  <Checkbox.Content className="flex flex-row gap-2 items-center">
                    <Checkbox.Control className="bg-transparent border-glass-border border">
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                    <span className="text-glass-foreground text-[11px] tracking-[0.2px] w-5/5">
                      By checking the box you agree to our{" "}
                      <Link to="/legal/terms-and-conditions" className="font-bold">
                        Terms and Conditions
                      </Link>
                    </span>
                  </Checkbox.Content>
                </Checkbox>
                <FieldError error={fieldErrors.terms} />
              </div>

              <div id="clerk-captcha" />
            </div>

            <div className="">
              <Button
                type="submit"
                className={"w-full disabled:opacity-100"}
                isPending={activeAction === "password"}
                isDisabled={isBusy}
              >
                {activeAction === "password" ? "Submitting…" : "Sign Up"}
              </Button>
              {/*
              <div className="flex gap-3 items-center justify-center py-4">
                <img src="/icons/line.svg" className="min-h-px" alt="" />
                <span className="text-glass-foreground uppercase text-xs">or</span>
                <img src="/icons/line.svg" className="min-h-px" alt="" />
              </div>

              <Button
                type="button"
                className={"bg-white text-off-black gap-3 font-semibold"}
                fullWidth
                onClick={handleGoogleSignUp}
                isPending={activeAction === "google"}
                isDisabled={isBusy}
              >
                {activeAction !== "google" && <GoogleIcon />} Continue with Google
              </Button> */}

              <div className="flex items-center text-sm text-center justify-center pt-4 gap-1 text-accent-foreground">
                <span>Already have an account?</span>
                <Link to="/sign-in">
                  <button type="button" className="underline">
                    Sign In
                  </button>
                </Link>
              </div>
            </div>
          </form>
        ) : (
          <form className="flex flex-col gap-10 lg:w-2/5 md:w-3/5 mx-auto" onSubmit={handleVerify} noValidate>
            <div className="flex flex-col gap-1">
              <Input
                placeholder="Verification code"
                className="auth-input"
                fullWidth
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={CODE_LENGTH}
                name="code"
                value={code}
                aria-invalid={Boolean(fieldErrors.code || errors?.fields?.code)}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH));
                  clearFieldError("code");
                }}
              />
              <FieldError error={fieldErrors.code || errors?.fields?.code} />
            </div>

            <div className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full"
                isPending={activeAction === "verify"}
                isDisabled={isBusy || code.length !== CODE_LENGTH}
              >
                {activeAction === "verify" ? "Verifying…" : "Verify Email"}
              </Button>

              <div className="flex items-center justify-between text-sm text-glass-foreground">
                <button
                  type="button"
                  className="underline disabled:opacity-50"
                  onClick={handleResendCode}
                  disabled={isBusy}
                >
                  {activeAction === "resend"
                    ? "Sending…"
                    : resendIn > 0
                      ? `Resend code (${resendIn}s)`
                      : "Resend code"}
                </button>

                <button type="button" className="underline" onClick={handleChangeEmail}>
                  Change email
                </button>
              </div>
            </div>
          </form>
        )}

        {formError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl bg-danger/15 border border-danger/30 px-4 py-3 text-sm text-danger lg:w-2/5 md:w-3/5 mx-auto"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{formError}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignUpPage;